/* Moteur de commission — le cœur du métier, isolé de l'interface et de la base.
 *
 * Omnix prend une commission à l'usine : soit un pourcentage du montant
 * facturé, soit un montant en euros par unité vendue. Le barème se pose par
 * usine et, au besoin, par gamme — la règle la plus précise l'emporte, et le
 * calcul se fait ligne à ligne, ce qui permet de mélanger les deux modes dans
 * une même vente.
 *
 * L'agent est payé SUR cette commission : sa part s'en déduit, elle ne s'y
 * ajoute pas. Omnix facture l'usine pour le total, l'agent refacture Omnix
 * pour sa part, et la différence reste à Omnix.
 */

import type {
  CommVente, Commission, EtapeVente, EtatCommission, ModePart, ModeRegle, PartAgent,
  RegleCommission, Vente,
} from "@/types/metier";
import type { Excedent } from "./prix";
import { SANS_EXCEDENT } from "./prix";

export function arrondi(v: number): number {
  return Math.round(v * 100) / 100;
}

/** Barème applicable à une gamme : la règle de gamme bat la règle d'usine. */
export function regleDe(
  regles: readonly RegleCommission[],
  usineId: string,
  gamme: string | null,
): RegleCommission | null {
  let large: RegleCommission | null = null;
  let precise: RegleCommission | null = null;
  for (const r of regles) {
    if (!r.actif || r.usine_id !== usineId) continue;
    if (r.gamme) {
      if (gamme && r.gamme === gamme) precise = r;
    } else large = r;
  }
  return precise ?? large;
}

/** Part d'un agent : une règle propre à une usine bat la règle générale. */
export function partDe(
  parts: readonly PartAgent[],
  agentId: string,
  usineId: string,
): PartAgent | null {
  let large: PartAgent | null = null;
  let precise: PartAgent | null = null;
  for (const p of parts) {
    if (!p.actif || p.agent_id !== agentId) continue;
    if (p.usine_id) {
      if (p.usine_id === usineId) precise = p;
    } else large = p;
  }
  return precise ?? large;
}

export function quantiteDe(vente: Pick<Vente, "lignes">): number {
  return (vente.lignes ?? []).reduce((s, l) => s + (l.qte || 0), 0);
}

export interface DetailCommission {
  base: number;
  quantite: number;
  montant: number;
  taux: number;
  mode: ModeRegle | "mixte";
  manuel: boolean;
  partManuelle: boolean;
  agentMontant: number;
  omnixMontant: number;
  /** Vendu au-dessus du plancher de l'agent. Informatif : il est déjà dans la base. */
  surplus: number;
  /** Ce que la règle d'excédent rend à l'agent, compris dans `agentMontant`. */
  surplusAgent: number;
  partMode: ModePart | null;
  partValeur: number;
}

/**
 * Détail complet d'une commission : ce qu'on prend à l'usine, ce que l'agent
 * garde, ce qui reste à Omnix.
 *
 * L'excédent — ce que l'agent a vendu au-dessus de son plancher — se calcule
 * à part (`excedentDe`) et s'ajoute à sa part. Il n'arrive pas d'ailleurs :
 * il sort de la commission encaissée, comme le reste de sa part, et le total
 * reste donc borné par ce qu'Omnix touche réellement.
 */
export function detailCommission(
  vente: Vente,
  regles: readonly RegleCommission[],
  parts: readonly PartAgent[],
  excedent: Excedent = SANS_EXCEDENT,
): DetailCommission {
  const base = vente.facture_usine ? vente.facture_usine.montant : vente.montant;
  const qte = quantiteDe(vente);
  const m: CommVente = vente.comm ?? {};
  const modes = new Set<ModeRegle>();
  let pct = 0;
  let unite = 0;

  if (m.mode) {
    modes.add(m.mode);
    const valeur = m.valeur ?? 0;
    if (m.mode === "pct") pct = (base * valeur) / 100;
    else unite = qte * valeur;
  } else {
    for (const l of vente.lignes ?? []) {
      const r = regleDe(regles, vente.usine_id, l.gamme);
      if (!r) continue;
      modes.add(r.mode);
      if (r.mode === "pct") pct += ((l.total || 0) * r.valeur) / 100;
      else unite += (l.qte || 0) * r.valeur;
    }
    // La facture de l'usine peut s'écarter du devis : seul le pourcentage suit,
    // un barème à l'unité ne dépend pas du prix.
    if (base !== vente.montant && vente.montant > 0) pct = (pct * base) / vente.montant;
  }

  const montant = arrondi(pct + unite);
  const liste = [...modes];
  const mode: ModeRegle | "mixte" = m.mode
    ? m.mode
    : liste.length > 1
      ? "mixte"
      : (liste[0] ?? "pct");

  const p = partDe(parts, vente.agent_id, vente.usine_id);
  const partMode: ModePart | null = m.partMode ?? p?.mode ?? null;
  const partValeur = m.partMode ? (m.partValeur ?? 0) : (p?.valeur ?? 0);

  let agent = 0;
  if (partMode === "points") agent = (base * partValeur) / 100;
  else if (partMode === "pct") agent = (montant * partValeur) / 100;
  else if (partMode === "unite") agent = qte * partValeur;

  /* La part d'excédent s'ajoute à la part de barème, puis l'ensemble est borné :
     une part, excédent compris, ne peut pas dépasser la commission encaissée —
     sinon Omnix paierait l'agent avec de l'argent qu'elle n'a pas reçu. Quand
     le plafond mord, c'est l'excédent qui cède en premier : la part de barème
     est due, l'excédent est un supplément. */
  const partBareme = Math.max(0, Math.min(arrondi(agent), montant));
  const surplusAgent = Math.max(0, Math.min(arrondi(excedent.part), montant - partBareme));
  const agentMontant = arrondi(partBareme + surplusAgent);

  return {
    base,
    quantite: qte,
    montant,
    taux: base > 0 ? arrondi((montant / base) * 100) : 0,
    mode,
    manuel: Boolean(m.mode),
    partManuelle: Boolean(m.partMode),
    agentMontant,
    omnixMontant: arrondi(montant - agentMontant),
    surplus: arrondi(excedent.montant),
    surplusAgent,
    partMode,
    partValeur,
  };
}

/* Les cinq états d'une commission, avec leur teinte. Au domaine plutôt qu'à
   l'écran : trois écrans les affichent, et il n'y a qu'une vérité. */
export const ETATS_COMMISSION: readonly { cle: EtatCommission; nom: string; ton: string }[] = [
  { cle: "brouillon", nom: "Brouillon", ton: "" },
  { cle: "confirmee", nom: "Confirmée", ton: "info" },
  { cle: "a_facturer", nom: "À facturer", ton: "accent" },
  { cle: "facturee", nom: "Facturée", ton: "warn" },
  { cle: "payee", nom: "Payée", ton: "ok" },
];

export function etatCommissionDe(etat: EtatCommission | string) {
  return ETATS_COMMISSION.find((e) => e.cle === etat) ?? { cle: etat, nom: etat, ton: "" };
}

/** Une commission facturée ou payée est figée : elle ne suit plus le barème. */
export function figee(c: Pick<Commission, "etat">): boolean {
  return c.etat === "facturee" || c.etat === "payee";
}

/** État de la commission déduit de l'étape de la vente. */
/* La commission naît quand le client commande, se confirme quand la facture
   de l'usine arrive, et devient exigible quand la vente est payée. Les étapes
   d'acheminement ne la font pas bouger : la marchandise voyage, l'argent non. */
export const ETAT_PAR_ETAPE: Record<string, EtatInitial> = {
  commande_client: "brouillon",
  commande_usine: "brouillon",
  expedie: "brouillon",
  livre: "brouillon",
  facture: "confirmee",
  paye: "a_facturer",
};
type EtatInitial = "brouillon" | "confirmee" | "a_facturer";

export const ORDRE_ETAT: readonly EtatCommission[] = [
  "brouillon", "confirmee", "a_facturer", "facturee", "payee",
];

/**
 * État que prend la commission quand la vente avance. Il suit l'étape de la
 * vente mais ne revient jamais en arrière : une commission qu'on a poussée à
 * la main vers « à facturer » y reste.
 */
export function etatApres(actuel: EtatCommission | null, etape: EtapeVente): EtatCommission {
  const vise: EtatCommission = ETAT_PAR_ETAPE[etape] ?? "brouillon";
  if (!actuel) return vise;
  return ORDRE_ETAT.indexOf(actuel) >= ORDRE_ETAT.indexOf(vise) ? actuel : vise;
}

/**
 * Ce que l'utilisateur voit de la commission prise à l'usine.
 *
 * Avec un seul niveau d'agent, « niveau » et « total » disent la même chose :
 * les niveaux qui voient l'usine, ce sont désormais tous les agents. Le
 * réglage qui tranche encore quelque chose, c'est « part » — chacun ne voit
 * que la sienne, et le total reste à la direction.
 */
export function voitTotal(
  niveau: "agent" | "admin",
  transparence: "part" | "niveau" | "total",
): boolean {
  if (transparence === "part") return niveau === "admin";
  return true;
}
